import type { AnyAction, Store } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { AUTH_REDUCER_KEY } from 'app-components/flux/auth';
import { getLogger, localPreferences } from 'app-components/utils/core';
import {
  asyncDispatchMiddleware,
  crashReporterMiddleware,
  inject,
  sagaMiddleware,
} from 'app-components/utils/redux-store';
import type CoreRootState from 'app-components/utils/redux-store/rootState';

import { actionLoggerMiddleware } from './actionLoggerMiddleware';
import { initListeners, listenerMiddleware } from './listenerMiddleware';
import { persistedAuthSession } from './persistedAuthSession';
import reducer from './reducers';
import { rootSaga } from './sagas';
import './listeners';

const logger = getLogger('store');

const HIDDEN_DEVTOOLS_ACTIONS = new Set<string>([
  'aiChatStore/appendStreamingDelta',
  'aiChatStore/appendReasoningDelta',
]);

// Starts nothing (no inject, sagas or listeners), so a failed call can be retried.
function buildStore(enableSagas: boolean, preloadedState: object) {
  return configureStore({
    // The saved session replaces any cached or passed auth state.
    preloadedState: { ...preloadedState, [AUTH_REDUCER_KEY]: persistedAuthSession },
    reducer,
    middleware(getDefaultMiddleware) {
      const middleware = getDefaultMiddleware({ serializableCheck: false, immutableCheck: false });
      // Runs after RTK's default middleware, so it only catches errors from the middleware below it and the reducers.
      middleware.push(crashReporterMiddleware);
      middleware.push(asyncDispatchMiddleware);
      middleware.push(actionLoggerMiddleware);
      middleware.push(listenerMiddleware.middleware);
      if (enableSagas) {
        middleware.push(sagaMiddleware);
      }
      return middleware;
    },
    devTools: localPreferences.getDebug()
      ? {
          trace: true,
          traceLimit: 20,
          predicate: (_state: unknown, action: AnyAction) => !HIDDEN_DEVTOOLS_ACTIONS.has(action.type),
        }
      : false,
  });
}

function startStore(store: ReturnType<typeof buildStore>, enableSagas: boolean) {
  // Core's RootState also declares slices web does not register (mileage tracker address).
  inject(store as Store<CoreRootState>);

  if (enableSagas) {
    sagaMiddleware.run(rootSaga);
  }

  logger.info('Dispatching initListeners...');
  store.dispatch(initListeners());
  logger.info('Store initialization complete');

  return store;
}

export function createStore(enableSagas = false, preloadedState = {}) {
  return startStore(buildStore(enableSagas, preloadedState), enableSagas);
}

/** The app's store, with sagas. Cached state that fails to build is dropped, and the store starts empty. */
export function createAppStore(cachedState: object): RootStore {
  let store: RootStore;
  try {
    store = buildStore(true, cachedState);
  } catch (error) {
    if (Object.keys(cachedState).length === 0) throw error;
    logger.error('Failed to build the store from cached state: ', error);
    store = buildStore(true, {});
  }
  return startStore(store, true);
}

export type RootStore = ReturnType<typeof createStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<RootStore['getState']>;
export type AppDispatch = RootStore['dispatch'];
